import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import {
  formatUserName,
  isUserDeleted,
  isUserFromCurrentAccount as isUserFromCurrentAccountUtil,
  canUseChildAccount,
  getLinkNavigationProps,
  type PerformanceWithAccountFilter,
} from "features/Library/Common/utils/performanceUtils";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
  type FetchPageFn,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type PeopleAcquisitionDetails } from "features/Reporting/types/people";
import { type ColumnOption } from "interfaces/columnOptions";
import { useCallback, useMemo } from "react";
import { fetchPeopleAcquisitionDetailsPage, type PeopleAcquisitionDrilldownFilters } from "./queries";
import { MoboLink } from "components/MoboLink/MoboLink";
import RouteNames from "enums/routeNames";
import styles from "./poepleAcquisitionDrilldown.module.scss";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { connect, type ConnectedProps } from "react-redux";

export type OwnProps = {
  filter: PerformanceWithAccountFilter;
  accountId: number;
  selectedCampaign: string;
  selectedMedium: string;
  selectedSource: string;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
  disableLinks?: boolean;
};

export type Props = OwnProps & PropsFromRedux;

export const PeopleAcquisitionDrilldownComponent = ({
  filter,
  accountId,
  selectedCampaign,
  selectedMedium,
  selectedSource,
  childAccountId,
  hasCreateAccountsAddOn,
  setHasExportData,
  disableLinks,
}: Props) => {
  const formatDateTime = useReportingFormatWithTimeZone();

  const updateDrilldownExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) => {
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. This medium has no interaction data.",
      });
    },
    [setHasExportData],
  );

  const columnOptions: (ColumnOption<PeopleAcquisitionDetails> & { sortKey?: string })[] = useMemo(
    () => [
      {
        name: "NAME",
        width: 6,
        isSortable: true,
        sortKey: "FirstName",
        render(item) {
          const formattedNameDisplay = formatUserName(item.FirstName, item.LastName);

          const isUserFromCurrentAccount = isUserFromCurrentAccountUtil(item.AccountId, accountId);
          const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
          const shouldShowLink =
            !isUserDeleted(item.FirstName, item.LastName) && (childAccountEnabled || !childAccountId) && !disableLinks;

          const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
            childAccountEnabled,
            isUserFromCurrentAccount,
            childAccountId,
            fallbackAccountId: item.AccountId,
          });

          const userName = (
            <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
          );

          return (
            <div>
              {shouldShowLink && (filter.type !== "sp" || isUserFromCurrentAccount) ? (
                <MoboLink
                  accountId={linkAccountId}
                  target={linkTarget}
                  to={`/${RouteNames.peopleUsers}/${item.UserId}`}
                  className={styles.linkButton}
                >
                  {userName}
                </MoboLink>
              ) : (
                userName
              )}
            </div>
          );
        },
      },
      {
        name: "DEPARTMENT",
        width: 5,
        isSortable: true,
        sortKey: "Department",
        render(item) {
          const department = item.Department || "-";
          return <Tooltip target={<TextTruncate>{department}</TextTruncate>} content={department} />;
        },
      },
      {
        name: "JOB TITLE",
        width: 5,
        isSortable: true,
        sortKey: "JobTitle",
        render(item) {
          const title = item.JobTitle || "-";
          return <Tooltip target={<TextTruncate>{title}</TextTruncate>} content={title} />;
        },
      },
      {
        name: "DATE",
        width: 4,
        isSortable: true,
        sortKey: "ClickedAt",
        render(item) {
          const formattedDate = formatDateTime(item.ClickedAt);
          const datePart = formattedDate.split(" ")[0];
          return <Tooltip target={<TextTruncate>{datePart}</TextTruncate>} content={datePart} />;
        },
      },
      {
        name: "TIME",
        width: 4,
        isSortable: true,
        sortKey: "ClickedAt",
        render(item) {
          const formattedDate = formatDateTime(item.ClickedAt);
          const timePart = formattedDate.split(" ").slice(1).join(" ");
          return <Tooltip target={<TextTruncate>{timePart}</TextTruncate>} content={timePart} />;
        },
      },
    ],
    [accountId, formatDateTime, childAccountId, hasCreateAccountsAddOn, disableLinks, filter.type],
  );

  const filters = useMemo<PeopleAcquisitionDrilldownFilters>(
    () => ({
      filter,
      selectedCampaign,
      selectedMedium,
      selectedSource,
    }),
    [filter, selectedCampaign, selectedMedium, selectedSource],
  );

  const fetchPage = useCallback<FetchPageFn<PeopleAcquisitionDetails>>(
    (params, signal) => fetchPeopleAcquisitionDetailsPage(filters, params, signal),
    [filters],
  );

  const queryKey = useMemo(
    () =>
      [
        "peopleAcquisitionUsersDrilldown",
        filters.filter,
        filters.selectedCampaign,
        filters.selectedMedium,
        filters.selectedSource,
      ] as const,
    [filters],
  );

  return (
    <div className="people-acquisition-drilldown">
      <GenericPerformanceListWithServerPagination
        title={selectedCampaign}
        columns={columnOptions}
        filter={filter}
        fetchPage={fetchPage}
        queryKeyBase={queryKey}
        hasDataForExport={updateDrilldownExportDataState}
      />
    </div>
  );
};

const mapStateToProps = (_state: RootState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

export const PeopleAcquisitionDrilldown = connector(PeopleAcquisitionDrilldownComponent);
